import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import { OrderInvoice } from './OrderInvoice';
import { OrderProduct } from './OrderProduct';
import { OrderFileUploader } from './OrderFileUploader';
import { OrderFileItem } from './OrderFileItem';
import { NotificationMessage } from './NotificationMessage';
import { ConfirmModal } from './ConfirmModal';

export const OrderModal = ({ order, closeModal, orderSent }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormLoading, setIsFormLoading] = useState(false);

    const [notification, setNotification] = useState({
        type: 'info',
        message: '',
        show: false,
    });

    const [uploadedFiles, setUploadedFiles] = useState(null);

    const [selectedTransporter, setSelectedTransporter] = useState("");
    const [selectedTable, setSelectedTable] = useState("Tabela de Estoque");
    const [comment, setComment] = useState("");
    const [tableError, setTableError] = useState(false);
    const [updatedItems, setUpdatedItems] = useState(Array(order.items.length).fill(false));
    const [allProductsUpdated, setAllProductsUpdated] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const fetchOrders = async () => {
        try {
            const response = await fetch(`${baseUrl}/orders/${order.id}/invoices`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os pedidos.');
            }

            const data = await response.json();
            setInvoices(data.data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const checkAllProductsUpdated = (index) => {
        const newUpdatedItems = [...updatedItems];
        newUpdatedItems[index] = true;
        setUpdatedItems(newUpdatedItems);

        const allUpdated = newUpdatedItems.every((updated) => updated);
        setAllProductsUpdated(allUpdated);
    };

    const handleTableChange = (e) => {
        setSelectedTable(e.target.value);
        setTableError(false);
    };

    const handleCommentChange = (e) => {
        setComment(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsFormLoading(true);
        
        if (selectedTable === "Tabela de Estoque") {
            setTableError(true);
            setIsFormLoading(false);
            return;
        }

        const transportInfo = `Tabela: ${selectedTable}`;
        const commentInfo = comment ? `<br>Comentário: ${comment}` : '';

        const requestBody = {
            obs: transportInfo + commentInfo,
        };

        try {
            const response = await fetch(`${baseUrl}/orders/${order.id}/supplier/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const sucessMessage = 'Pedido enviado com sucesso!' ;

                setIsFormLoading(false);

                closeModal();
                orderSent(sucessMessage);
            } else {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });
                setIsFormLoading(false);

                console.error('Erro ao enviar o pedido:', response.statusText);
            }
        } catch (error) {
            console.error("Erro ao enviar o pedido:", error);
            setIsFormLoading(false);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const stockTables = [
        { id: 1, valor: 'Geral' },
        { id: 2, valor: 'CD SP' },
    ];

    const fetchUploadedFiles = async () => {
        try {
            const response = await fetch(`${baseUrl}/orders/${order.id}/files`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os arquivos.');
            }

            const data = await response.json();
            setUploadedFiles(data.data);
        } catch (error) {
            console.error(error.message);
        }
    };

    const handleUploadComplete = () => {
        fetchUploadedFiles();
    };

    useEffect(() => {
        fetchUploadedFiles();
    }, []);

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, show: false }));
    };

    const openCancelModal = () => {
        setIsCancelModalOpen(true);
    };

    const closeCancelModal = () => {
        setIsCancelModalOpen(false);
    };

    const handleCancelChange = () => {
        fetchOrders();

        setNotification({
            type: 'alert',
            message: 'NF cancelada com sucesso!',
            show: true,
        });
    }

    const handleReplaceChange = () => {
        fetchOrders();
        
        setNotification({
            type: 'success',
            message: 'NF substituída com sucesso!',
            show: true,
        });
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[99999]">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeModal}></div>
            <div className="animate-fade-in-down [animation-duration:_0.1s] rounded-sm border border-stroke bg-white w-full max-w-5xl max-h-[90%] overflow-y-auto p-5 2xl:p-6 shadow-md relative">
                <span className="absolute top-0 bottom-0 right-0 p-4" onClick={closeModal}>
                    <svg className="fill-current h-5 w-5 text-slate-200" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <title>Fechar</title>
                        <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
                    </svg>
                </span>

                <h2 className="text-xl 2xl:text-2xl font-bold 2xl:mb-1 mr-10">Pedido: {order.order_number}</h2>
                <p className="text-slate-500 mb-6 2xl:mb-8">Fornecedor: {order.supplier.erp_id}</p>

                {loading ? (
                    <div className="mx-auto py-10">
                        <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
                    </div>
                ) : (
                    <>
                        <form onSubmit={handleSubmit}>
                            <div className="rounded-[5px] border border-stroke p-3 pt-0 mb-7 2xl:mb-10">
                                <div className="mb-5.5 flex flex-col justify-between">
                                    <h5 className="font-bold text-black bg-white -mt-4 px-2 2xl:-mt-5 p-1 2xl:p-2 w-fit">Transportadora</h5>

                                    <div className="flex items-center gap-x-2">
                                        <input 
                                            className="border text-slate-500 border-gray-300 p-1 w-2/6" 
                                            value={order.shippings[0].transporter.name} 
                                            disabled 
                                        />
                                        <h5 className="text-sm font-medium ml-2">Valor do frete:</h5>
                                        <input 
                                            className="border text-slate-500 border-gray-300 p-1 w-1/6" 
                                            value={order.shippings[0].formatted_value} 
                                            disabled 
                                        />
                                        {order.status === 'waiting' && (
                                            <select 
                                                name="tabela_estoque"
                                                className={`border ${tableError ? 'border-red-500' : 'border-gray-300'} p-1 w-3/12`}
                                                onChange={handleTableChange}
                                                value={selectedTable}
                                            >
                                                <option value="Tabela de Estoque" disabled>Tabela de Estoque</option>
                                                {stockTables.map((table) => (
                                                    <option key={table.id} value={table.valor}>
                                                        {table.valor}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-[5px] border border-stroke p-3 pt-0">
                                <div className="mb-5.5 flex flex-col justify-between">
                                    <h5 className="font-bold text-black bg-white -mt-4 px-2 2xl:-mt-5 p-1 2xl:p-2 w-fit">Produtos</h5>

                                    <div>
                                        <table className="w-full">
                                            <thead>
                                                <tr>
                                                    <th className="pb-1 2xl:py-2 px-2 2xl:px-4 text-left">Nome</th>
                                                    <th className="pb-1 2xl:py-2 px-2 2xl:px-4 text-left">SKU</th>
                                                    <th className="pb-1 2xl:py-2 px-2 2xl:px-4 text-left">Qtd</th>
                                                    <th className="pb-1 2xl:py-2 px-2 2xl:px-4 text-left">Fornecedor</th>
                                                    <th className="pb-1 2xl:py-2 px-2 2xl:px-4 pr-0 text-left">Preço de custo</th>
                                                </tr>
                                            </thead>
                                            
                                            <tbody>
                                                {order.items.map((item, index) => (
                                                    <OrderProduct
                                                        key={index}
                                                        item={item}
                                                        orderId={order.id}
                                                        supplier={order.supplier}
                                                        index={index}
                                                        isFetchable={order.status === 'waiting' ? true : false}
                                                        checkAllProductsUpdated={checkAllProductsUpdated}
                                                        updatedItems={updatedItems}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {invoices && invoices.length > 0 && (
                                <div className="rounded-[5px] border border-stroke p-3 pt-0 mt-7 2xl:mt-10">
                                    <div className="mb-5.5 flex flex-col justify-between">
                                        <h5 className="font-bold text-black bg-white -mt-4 px-2 2xl:-mt-5 p-1 2xl:p-2 w-fit">Notas</h5>
                                        <div className="overflow-y-auto max-h-80">
                                            <table className="w-full">                                        
                                                <tbody>
                                                    {invoices.map((invoice, index) => (
                                                        <OrderInvoice key={index} invoice={invoice} index={index} orderId={order.id} handleCancelChange={handleCancelChange} handleReplaceChange={handleReplaceChange} />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {order.status === 'invoiced_supplier' || order.status === 'shipped' ? (
                                <div className="mt-4">
                                    {uploadedFiles && uploadedFiles.length > 0 && (
                                    <div className="rounded-[5px] border border-stroke p-3 pt-0 mt-7 2xl:mt-10">
                                        <div className="mb-5.5 flex flex-col justify-between">
                                            <h5 className="font-bold text-black bg-white -mt-4 px-2 2xl:-mt-5 p-1 2xl:p-2 w-fit">Arquivos</h5>
                                            <ul className="flex flex-wrap gap-x-2">
                                                {uploadedFiles.map(file => (
                                                    <OrderFileItem key={file.id} file={file} />
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    )}

                                    <OrderFileUploader orderId={order.id} onUploadComplete={handleUploadComplete} />
                                </div>
                            ) : null}

                            {order.status === 'waiting' && (
                                <div className="mt-4 mb-2">
                                    <textarea
                                        className="border border-gray-300 p-2 rounded-[5px] w-full"
                                        placeholder="Comentário"
                                        value={comment}
                                        onChange={handleCommentChange}
                                    />
                                </div>
                            )}

                            {order.status !== 'canceled' && (
                                <div className="mx-auto flex gap-x-6 justify-center max-w-xl mt-4">
                                    {order.status === 'waiting' && (
                                        <div>
                                            <button
                                                type="submit"
                                                className={`relative block w-full bg-primary border border-primary text-white py-2 px-10 rounded transition-all duration-300 mr-2 hover:bg-opacity-90 disabled:bg-opacity-50 disabled:border-opacity-50 disabled:cursor-not-allowed${isFormLoading ? ' text-opacity-0' : ''}`}
                                                disabled={!allProductsUpdated}
                                            >
                                                Enviar pedido
                                                {isFormLoading && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
                                                    </div>
                                                )}
                                            </button>
                                            {isFormLoading && (
                                                <div className="absolute inset-0 cursor-not-allowed" />
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <button
                                            onClick={openCancelModal}
                                            className="block w-full bg-red-500 border border-red-500 text-white py-2 px-10 rounded transition-all duration-300 mr-2 hover:bg-opacity-90"
                                        >
                                            Cancelar pedido
                                        </button>
                                    </div>

                                    {isCancelModalOpen && <ConfirmModal icon={faTimes} closeModal={closeCancelModal} type="cancelOrder" confirm={`/orders/${order.id}/cancel`} />}
                                </div>
                            )}
                        </form>
                    </>
                )}
            </div>

            {notification && notification.show && (
                <NotificationMessage
                    type={notification.type}
                    message={notification.message}
                    show={notification.show}
                    onClose={handleCloseNotification}
                />
            )}
        </div>
    );
};